import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { onPendingSubtaskSelection, takePendingSubtaskSelection } from '../../utils/pendingSubtaskSelection'
import { stepLanes } from '../../utils/moveBySteps'
import { isOutsideCard, SubtaskDragOutContext } from '../../utils/subtaskDragOut'
import type { Point } from '../../utils/nestTarget'
import { useTranslation } from 'react-i18next'
import { DndContext, DragOverlay, MeasuringStrategy, useSensor, useSensors, closestCenter, type CollisionDetection, type DragEndEvent, type DragMoveEvent, type DragStartEvent, type MeasuringConfiguration, type Modifier } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { afterIdForDrop } from '@/src/renderer/lib/db/subtaskOrder'
import { RowPointerSensor } from './rowPointerSensor'
import { afterIdForGroupDrop, dragGroup } from './dragGroup'
import { branchHeads, descendantRows, lastOfBranch, siblingRows, treeLines } from './subtaskTreeLayout'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { KeysHint } from '@/src/renderer/components/common/KeysHint'
import { useShortcutText } from '@/src/renderer/hooks/useShortcutText'
import { arriveMotion } from '@/src/renderer/lib/motion/arriveMotion'
import { MotionRow } from './MotionRow'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import { TaskCheck } from '../TaskCheck'
import { OpenRow } from './OpenRow'
import { SubtaskRow } from './SubtaskRow'
import { SelectionHull } from '../SelectionHull'
import { SubtaskMenu, taskMenuMarks } from '../TaskMenu'
import type { SelectionModifiers, SubtaskListProps } from './SubtaskList.types'
import type { SubtaskChanges, SubtaskItem } from '../../hooks/useSubtaskEditor'
import type { TaskMenuTarget } from '../TaskMenu/TaskMenu.types'
import { wakeTaskUpdate } from '../../utils/buildTaskUpdate'
import { DormantIcon } from '@/src/renderer/components/icons'
import './SubtaskList.scss'

/** Der Schlüssel, unter dem die **offene Zeile** über alle Plätze hinweg dieselbe bleibt. */
const OPEN_ROW_KEY = 'open-row'

/** Und der ihres **Abbilds**, das am alten Platz zufährt. */
const GHOST_ROW_KEY = 'open-row-ghost'

/** Eine Checkliste wird nur auf und ab sortiert — seitliches Wandern wäre Zufall. */
const onlyVertical: Modifier = ({ transform }) => ({ ...transform, x: 0 })

/**
 * Zieht man mehrere, klappen die übrigen nach dem Anfassen zu. Die beim Start gemessenen
 * Rechtecke stimmen dann nicht mehr — deshalb wird während des Zugs fortlaufend gemessen.
 */
const MEASURE_ALWAYS: MeasuringConfiguration = { droppable: { strategy: MeasuringStrategy.Always } }


/**
 * Die Tasten-Leiste unter der Checkliste blendet sich in ihrem festen Platz ein und aus — 4 px von
 * unten, ohne Verzug: Neben ihr läuft keine größere Bewegung.
 */
const keysArrive = arriveMotion({ distance: 4, delay: 0 })
const keysStill = arriveMotion({ reduced: true })

// Dasselbe Objekt bei jedem Rendern — sonst bekäme jede Zeile mit `useSortable` neue Sensoren (#303, `useTaskDragAndDrop`).
const ROW_POINTER_ACTIVATION = { activationConstraint: { distance: 5 } }

/** Abstand zwischen den Sparks mit abgehakter Unteraufgaben — der erste folgt dem Haken der Aufgabe. */
export const SPARK_STEP_MS = 90

/**
 * Das Bild am Zeiger beim Herausziehen (#204): eine Kopie der echten Zeile, so wie sie
 * dasteht — angefasst, mit allen Marken (gewünscht 27.09.2026: „soll einfach selektiert
 * aussehen“). Kopiert statt neu gebaut, damit Bild und Zeile nie auseinanderlaufen.
 */
const RowImage: React.FC<{ id: string }> = ({ id }) => {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const row = document.querySelector(`.subtask-list [data-subtask-id="${CSS.escape(id)}"]`)
    const host = ref.current
    if (!row || !host) return
    const copy = row.cloneNode(true) as HTMLElement
    copy.classList.remove('subtask-row--lifted', 'subtask-row--away', 'subtask-row--dragging')
    // Am Zeiger ohne Einrückung und Linien (#211): Das Bild hängt an der Hand, nicht im Baum.
    copy.classList.forEach(name => { if (/^subtask-row--(l\d|tree)$/.test(name)) copy.classList.remove(name) })
    copy.querySelectorAll('.subtask-tree__line').forEach(line => line.remove())
    copy.classList.add('subtask-row--selected')
    copy.removeAttribute('style')
    copy.removeAttribute('data-subtask-id')
    host.replaceChildren(copy)
  }, [id])
  return <div ref={ref} className="subtask-drag-overlay" />
}

/**
 * The checklist under an expanded task (Figma 2172-15474 / 2172-16113).
 *
 * Es gibt immer **genau eine offene Zeile**: am Ende der Liste, oder dort, wo
 * Cmd/Ctrl+Enter sie gerade eingeschoben hat. Mit keinen Einträgen IST sie die
 * ganze Komponente — was der Aufbau inline neben die Notizen stellt.
 *
 * Enter auf der offenen Zeile hängt an, so dass sich eine ganze Liste in einem
 * Zug tippen lässt; Enter auf einer leeren beendet die Kette, statt eine leere
 * Zeile zu hinterlassen. Sie leert sich erst, wenn die neue Zeile wirklich
 * dasteht — siehe `sent` in `OpenRow`.
 *
 * @example
 * <SubtaskList items={checklist.items} onAdd={checklist.add} … />
 */
export const SubtaskList: React.FC<SubtaskListProps> = ({
  items, onAdd, onInsertAfter, onMove, onToggle, onRename, onRemove, onRestore, onOpen, onOpenComments, onOpenReminders, coupledSpark = false, parentId, onUnnest,
  onUpdate, onMoveTo, parentRecurring = false, onDuplicate, onIndent, onOutdent,
}) => {
  const { t } = useTranslation()
  const inputs = useRef(new Map<string, HTMLInputElement>())
  const openInput = useRef<HTMLInputElement>(null)

  /**
   * Hinter welcher Zeile die **offene Zeile** steht; `null` heißt am Ende.
   *
   * Sie ist ein Zustand und keine feste Stelle mehr, seit Cmd/Ctrl+Enter sie in
   * die Mitte holen kann. Es gibt sie trotzdem nur einmal: Zwei gestrichelte
   * Haken übereinander sähen aus wie ein Fehler, und die Liste spränge in der
   * Höhe.
   */
  const [openAfterId, setOpenAfterId] = useState<string | null>(null)

  /**
   * Welche Zeilen angefasst sind und welche offen steht (Nutzer-Vorgabe,
   * 2026-09-20). Ein Klick **wählt**, Doppelklick, Enter oder das erste
   * getippte Zeichen **öffnet** — wie eine **Aufgabe** in der Liste und ein
   * Zettel auf der **Pinnwand** (CONTEXT.md).
   *
   * **Mehrere zugleich**, mit denselben Tasten wie die Aufgabenliste: Cmd/Strg
   * nimmt einzeln dazu, Shift den Bereich ab dem **Anker** — das ist die
   * zuletzt ohne Shift angefasste Zeile. Offen ist trotzdem immer höchstens
   * eine; geschrieben wird in einer Zeile, nicht in dreien.
   */
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set())
  const [editingId, setEditingId] = useState<string | null>(null)
  const anchorId = useRef<string | null>(null)
  // Für Handler, die beim Druck und beim Anfassen den Stand brauchen, ohne neu zu entstehen.
  const selectedRef = useRef(selectedIds)
  selectedRef.current = selectedIds

  /**
   * Die Zeile, auf die gedrückt wurde, während sie zu einer Mehrfachauswahl gehörte. Wird aus dem
   * Druck kein Zug, wählt das Loslassen nur sie (gemeldet 28.09.2026: Beim Ziehen ging nur eine
   * mit — der Druck hatte die Auswahl schon auf sie zusammenfallen lassen).
   */
  const collapseOnRelease = useRef<string | null>(null)

  /**
   * **Das Aufgaben-Menü an einer Zeile** (#205): Solange es offen ist, liegt der Fokus darin —
   * die Auswahl, die es meint, bleibt trotzdem stehen. Was die Zeilen umräumt (Löschen,
   * Herauslösen, Umhängen), läuft erst nach dem Schließen (`afterMenu`): Dann gibt es keinen
   * Streit mit dem Menü darum, wohin der Caret geht.
   */
  const menuOpen = useRef(false)
  const afterMenu = useRef<(() => void) | null>(null)

  // Die Zeile, hinter der die offene stand, kann verschwinden (gelöscht, oder
  // von einem zweiten Gerät genommen) — dann gehört die offene Zeile ans Ende.
  useEffect(() => {
    if (openAfterId && !items.some(item => item.id === openAfterId)) setOpenAfterId(null)
  }, [items, openAfterId])

  /**
   * Die frisch eingeschobene Zeile, die noch auf ihr erstes Bild wartet.
   *
   * Die Datenquelle antwortet, bevor React gerendert hat — ihr Feld gibt es in
   * dem Moment noch nicht. Der Effekt unten greift zu, sobald es dasteht.
   */
  const [insertedId, setInsertedId] = useState<string | null>(null)

  /**
   * Wie viele Zeilen dastanden, als am ENDE eine abgeschickt wurde — `null`,
   * wenn gerade nichts unterwegs ist.
   *
   * Die Kette dort lebt davon, dass der Caret in der **offenen Zeile**
   * bleibt: tippen, Enter, tippen. Im E2E-Lauf fiel er nach der ersten Zeile auf
   * `body`, und die zweite ging ins Leere (21.09.2026). Darauf zu bauen, dass
   * ihn niemand anfasst — weder das Erscheinen der neuen Zeile noch deren
   * Bewegung —, hat nicht getragen; die Liste holt ihn jetzt selbst zurück,
   * sobald die Zeile steht.
   */
  const [appendedAt, setAppendedAt] = useState<number | null>(null)

  const sensors = useSensors(useSensor(RowPointerSensor, ROW_POINTER_ACTIVATION))

  const registerInput = useCallback((id: string, el: HTMLInputElement | null) => {
    if (el) inputs.current.set(id, el)
    else inputs.current.delete(id)
  }, [])

  /**
   * Ob der Fokus gerade absichtlich innerhalb der Liste umzieht.
   *
   * `focus()` feuert synchron erst den Abschied der alten Zeile — und dabei
   * steht das neue Ziel nicht überall im `relatedTarget`. Ohne diesen Merker
   * hielte der Aufräumer unten jeden Pfeildruck für ein Verlassen der Liste und
   * nähme Auswahl **und Anker** mit.
   */
  const movingCaret = useRef(false)

  /** Ob eine Zeile gerade im Baum wandert (⌥→ / ⌥←) — ihr kurzer Fokusverlust ist kein Verlassen. */
  const keepSelection = useRef(false)

  const putCaretAt = (el: HTMLInputElement | null) => {
    if (!el) return
    movingCaret.current = true
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
    movingCaret.current = false
  }

  /**
   * Übergibt Zeiger, Auswahl und Schreibmodus an die Nachbarzeile, bevor diese
   * hier verschwindet.
   *
   * **Alle drei zusammen** (gemeldet 2026-09-20): Der Zeiger allein reichte
   * nicht — die Zeile, in der er landete, stand ungetönt da und war stumm, weil
   * `selectedIds` und `editingId` noch auf die gelöschte zeigten. Wer beim
   * Tippen eine Zeile leerräumt, schreibt in der nächsten weiter; deshalb zieht
   * der Schreibmodus mit, wenn er vorher an war.
   */
  const handOverTo = useCallback((target: { id: string } | undefined) => {
    if (!target) {
      // Die offene Zeile ist ohnehin der Ort der nächsten — dort läuft er weiter.
      setSelectedIds(new Set())
      setEditingId(null)
      anchorId.current = null
      openInput.current?.focus()
      return
    }
    setSelectedIds(new Set([target.id]))
    setEditingId(current => (current === null ? null : target.id))
    anchorId.current = target.id
    putCaretAt(inputs.current.get(target.id) ?? null)
  }, [])

  // Called just before a row disappears, so the caret lands where the user was
  // rather than falling out of the card entirely.
  const focusPrevious = useCallback((id: string) => {
    const index = items.findIndex(item => item.id === id)
    // Die ERSTE Zeile hat keine über sich, und bis zum 19.09.2026 ging der
    // Caret schlicht mit ihr unter: Wer seine einzige Unteraufgabe leer
    // räumte, konnte danach nicht weiterschreiben.
    handOverTo(index > 0 ? items[index - 1] : undefined)
  }, [items, handOverTo])

  // Der Weg, in den Entf zeigt: nach unten. Am Ende der Liste wartet die offene Zeile.
  const focusNext = useCallback((id: string) => {
    const index = items.findIndex(item => item.id === id)
    handOverTo(index >= 0 ? items[index + 1] : undefined)
  }, [items, handOverTo])

  /**
   * Ob am Listenende gerade ein **Abbild** der offenen Zeile zufährt.
   *
   * Sie selbst wandert nur — ein Element mit festem Schlüssel, das seinen Platz
   * wechselt (nur so behält sie ihren Text). Unten bliebe deshalb einfach eine
   * Lücke, während oben etwas aufgeht: zwei Hälften derselben Bewegung, von
   * denen nur eine zu sehen ist. Das Abbild steht ein Bild lang da und fährt
   * dann zu.
   */
  const [ghostBelow, setGhostBelow] = useState(false)
  useEffect(() => {
    if (ghostBelow) setGhostBelow(false)
  }, [ghostBelow])

  const handleOpenBelow = useCallback((id: string) => {
    /*
      **Auf derselben Ebene darunter** (#181): Die neue Zeile wird ein Geschwister und steht deshalb
      hinter dem Ast der Zeile. Nur auf Ebene 1 ist „ganz unten“ derselbe Platz wie die offene Zeile
      am Ende.
    */
    const onTop = (items.find(item => item.id === id)?.depth ?? 1) === 1
    const isLast = onTop && items[items.length - 1]?.id === lastOfBranch(items, id)?.id
    // In der letzten Zeile gibt es nichts einzuschieben — die offene Zeile steht
    // dort schon. Sonst stünden zwei leere Zeilen übereinander.
    setOpenAfterId(isLast ? null : id)
    // Sie verlässt das Listenende: Dort fährt ihr Abbild zu.
    if (!isLast && openAfterId === null) setGhostBelow(true)
    requestAnimationFrame(() => openInput.current?.focus())
  }, [items, openAfterId])

  /** Die Zeilen zwischen zwei ids, beide eingeschlossen — der Shift-Bereich. */
  const rangeBetween = useCallback((fromId: string, toId: string) => {
    const from = items.findIndex(item => item.id === fromId)
    const to = items.findIndex(item => item.id === toId)
    if (from === -1 || to === -1) return [toId]
    const [start, end] = from < to ? [from, to] : [to, from]
    return items.slice(start, end + 1).map(item => item.id)
  }, [items])

  const handleSelect = useCallback((id: string, modifiers?: SelectionModifiers) => {
    /*
      Zieht der Zeiger gerade absichtlich um (Pfeiltaste, Löschen), steht die
      Auswahl schon — und zwar richtig. Der `onFocus` der Zielzeile käme sonst
      hinterher und ließe einen Shift-Bereich wieder auf eine Zeile zusammenfallen.
    */
    if (movingCaret.current) return
    collapseOnRelease.current = null

    // Eine andere Zeile anzufassen schließt die offene — zwei zugleich gibt es nicht.
    setEditingId(current => (current === id ? current : null))

    // Rechtsklick in die Auswahl: Sie bleibt, das Menü meint sie ganz.
    if (modifiers?.context) {
      if (selectedRef.current.has(id)) return
      setSelectedIds(new Set([id]))
      anchorId.current = id
      return
    }

    // Ein Druck in die Mehrfachauswahl: Sie bleibt, bis klar ist, ob gezogen wird.
    const selection = selectedRef.current
    if (modifiers?.press && !modifiers.toggle && !modifiers.range && selection.has(id) && selection.size > 1) {
      collapseOnRelease.current = id
      return
    }

    if (modifiers?.range && anchorId.current) {
      const range = rangeBetween(anchorId.current, id)
      setSelectedIds(prev => (modifiers.toggle ? new Set([...prev, ...range]) : new Set(range)))
      // Der Anker bleibt stehen: Von ihm aus greift der nächste Shift-Klick weiter.
      return
    }

    if (modifiers?.toggle) {
      setSelectedIds(prev => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
      anchorId.current = id
      return
    }

    setSelectedIds(new Set([id]))
    anchorId.current = id
  }, [rangeBetween])

  const handleRelease = useCallback((id: string) => {
    if (collapseOnRelease.current !== id) return
    collapseOnRelease.current = null
    setSelectedIds(new Set([id]))
    anchorId.current = id
  }, [])

  const handleEdit = useCallback((id: string) => {
    // Aufmachen heißt: genau diese. Eine Mehrfachauswahl fällt dabei zusammen.
    setSelectedIds(new Set([id]))
    anchorId.current = id
    setEditingId(id)
  }, [])

  const handleEndEdit = useCallback(() => setEditingId(null), [])

  /**
   * Wer die Liste verlässt, lässt nichts angefasst zurück (gemeldet 2026-09-20).
   *
   * Die Auswahl hängt am Fokus — also muss sie mit ihm gehen. Ohne das stand die
   * erste Unteraufgabe noch getönt da, während der Caret längst im
   * Notizfeld war: zwei Orte, die gleichzeitig behaupten, DER Ort zu sein.
   *
   * Am Container und nicht an der Zeile, weil nur hier zu sehen ist, wohin der
   * Fokus geht. Bleibt er in einer anderen **Unteraufgabe**, bleibt die Auswahl
   * (sonst flackerte sie bei jedem Pfeildruck); die **offene Zeile** zählt
   * dabei nicht mit — sie ist keine.
   */
  const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    // Das Menü einer Zeile nimmt den Fokus — die Auswahl, die es meint, bleibt (siehe unten).
    // Ebenso beim Ein- und Ausrücken: Die Zeile wandert im Baum und verliert kurz den Fokus.
    if (movingCaret.current || menuOpen.current || keepSelection.current) return
    const next = e.relatedTarget as HTMLElement | null
    if (next?.closest('.subtask-row:not(.subtask-row--open)')) return
    setSelectedIds(new Set())
    setEditingId(null)
    anchorId.current = null
  }, [])

  /** ↑/↓ im Auswahl-Modus; `false` am Rand, damit die Feldkette übernimmt. */
  const handleStep = useCallback((id: string, direction: -1 | 1, extend: boolean) => {
    const index = items.findIndex(item => item.id === id)
    const next = index === -1 ? undefined : items[index + direction]
    if (!next) return false
    // Mit Shift wächst der Bereich ab dem Anker, ohne ihn springt die Auswahl mit.
    if (extend && anchorId.current) setSelectedIds(new Set(rangeBetween(anchorId.current, next.id)))
    else { setSelectedIds(new Set([next.id])); anchorId.current = next.id }
    /*
      **Der Schreibmodus wandert mit** (Nutzer-Vorgabe, 2026-09-20): Wer in einer
      Zeile schreibt und ↓ drückt, will in der nächsten weiterschreiben — nicht
      erst wieder aufmachen. Verlassen wird mit Enter oder Escape, und dann
      bleibt die Zeile angefasst.
    */
    setEditingId(current => (current === null ? null : next.id))
    putCaretAt(inputs.current.get(next.id) ?? null)
    return true
  }, [items, rangeBetween])

  const handleNudge = useCallback((id: string, direction: -1 | 1) => {
    if (!onMove) return
    /*
      **Mehrere angefasst: alle rücken** (#204, Unter-Plan Tastatur) — jede für sich, Lücken
      bleiben; stößt die Auswahl an den Rand, rührt sich nichts. Geschrieben wird nacheinander,
      von oben nach unten: Jede Zeile kommt hinter ihren neuen Vorgänger, der schon steht.

      **Nur unter Geschwistern** (#181): Jede Zeile rückt in ihrer Geschwistergruppe, ihr Ast zieht
      mit; die Ebene wechseln ⌥→ und ⌥←.
    */
    const heads = selectedIds.has(id) && selectedIds.size > 1
      ? branchHeads(items, selectedIds)
      : items.filter(item => item.id === id)
    const moving = new Set(heads.map(item => item.id))
    const lanes = [...new Set(heads.map(head => siblingRows(items, head.id).map(item => item.id).join('\n')))]
      .map(lane => lane.split('\n'))
    const next = stepLanes(lanes, moving, direction)
    if (!next) return
    void (async () => {
      for (const lane of next) {
        for (const [i, moved] of lane.entries()) {
          if (moving.has(moved)) await onMove(moved, lane[i - 1] ?? null)
        }
      }
      // Der Caret bleibt, wo er ist — die Zeile wandert unter ihm weg.
      requestAnimationFrame(() => putCaretAt(inputs.current.get(id) ?? null))
    })()
  }, [items, onMove, selectedIds])

  /**
   * **⌥→ / ⌥←** (#181) an der angefassten Zeile — gehört sie zur Auswahl, an allen (ohne die, die
   * im Ast einer anderen mitgehen). Auswahl und Caret bleiben, die Zeile wandert im Baum.
   */
  const handleLevel = useMemo(() => (onIndent && onOutdent
    ? (id: string, direction: 'indent' | 'outdent') => {
        const ids = selectedIds.has(id) ? branchHeads(items, selectedIds).map(item => item.id) : [id]
        keepSelection.current = true
        void (direction === 'indent' ? onIndent(ids) : onOutdent(ids)).finally(() => {
          requestAnimationFrame(() => {
            putCaretAt(inputs.current.get(id) ?? null)
            keepSelection.current = false
          })
        })
      }
    : undefined), [onIndent, onOutdent, items, selectedIds])

  /**
   * Nimmt die genannten Zeilen und bietet EINEN Rückweg für alle.
   *
   * `withUndo` trennt die beiden Wege: Beim Tippen ist eine leergeräumte Zeile
   * ein Korrekturschritt (still), auf einer ausgewählten Zeile ein bewusster
   * Griff (mit Toast).
   */
  const removeMany = useCallback(async (ids: string[], withUndo: boolean) => {
    // Eine Zeile nimmt ihren Ast mit (#181, Grill 10); was darin angefasst ist, geht ohnehin mit.
    const removed = branchHeads(items, ids)
    if (removed.length === 0) return
    const below = removed.reduce((sum, item) => sum + descendantRows(items, item.id).length, 0)
    const total = removed.length + below
    /*
      **Erst löschen, dann melden** (ADR 0028): `onRemove` schreibt, und jeder
      Schreibvorgang lässt die gemerkte Handlung vergessen. Ohne das `await`
      landeten die Schreibvorgänge NACH `showUndoToast` und nähmen dem `⌘Z`
      genau das weg, was die Meldung eben angeboten hat.
    */
    await Promise.all(removed.map(item => onRemove(item.id)))
    if (!withUndo || !onRestore) return
    const message = removed.length > 1
      ? t('taskItem.subtaskDeletedMultiple', { count: total })
      : below > 0
        ? t('taskItem.subtaskDeletedWithBranch', { title: removed[0]!.title, count: below })
        : t('taskItem.subtaskDeleted', { title: removed[0]!.title })
    showUndoToast({
      message,
      undoLabel: t('common.undo'),
      undoneMessage: total === 1
        ? t('undo.restored.subtask')
        : t('undo.restored.subtaskMultiple', { count: total }),
      // Von hinten nach vorn: So trifft jede Zeile beim Zurückkommen die
      // Nachbarn an, zwischen denen sie stand.
      onUndo: () => { [...removed].reverse().forEach(item => onRestore(item)) },
    })
  }, [items, onRemove, onRestore, t])

  /** Wohin Caret und Auswahl gehen, wenn `heads` samt Ast verschwinden: nach unten bzw. oben daneben. */
  const neighboursOf = useCallback((heads: readonly { id: string }[]) => {
    const gone = new Set(heads.flatMap(head => [head.id, ...descendantRows(items, head.id).map(item => item.id)]))
    const first = items.findIndex(item => item.id === heads[0]?.id)
    const last = items.findIndex(item => item.id === lastOfBranch(items, heads[heads.length - 1]?.id ?? '')?.id)
    return {
      above: items.slice(0, Math.max(first, 0)).reverse().find(item => !gone.has(item.id)),
      below: items.slice(last + 1).find(item => !gone.has(item.id)),
    }
  }, [items])

  /*
    ⇧⌥↑/↓: die Auswahl heraus (gehört die Zeile nicht dazu, nur sie). Die Auswahl geht vorher an
    die Nachbarzeile, wie beim Löschen — der Caret bleibt in der Liste.
  */
  const handleUnnest = useMemo(() => (onUnnest
    ? (id: string, where: 'above' | 'below') => {
        // Ihr Ast geht mit (#181) — angefasste Kinder darin nicht noch einmal einzeln.
        const heads = selectedIds.has(id) ? branchHeads(items, selectedIds) : items.filter(item => item.id === id)
        const { above, below } = neighboursOf(heads)
        handOverTo(below ?? above)
        onUnnest(heads.map(item => item.id), where)
      }
    : undefined), [onUnnest, items, selectedIds, handOverTo, neighboursOf])

  const handleRemove = useCallback((id: string, withUndo: boolean) => {
    void removeMany([id], withUndo)
  }, [removeMany])

  /** Nimmt `ids` (in Listenreihenfolge) samt Ast mit Rückweg; Caret und Auswahl gehen zur Nachbarzeile. */
  const removeRows = useCallback((ids: string[], towards: -1 | 1) => {
    const heads = branchHeads(items, ids)
    if (heads.length === 0) return

    /*
      Wohin Zeiger und Auswahl danach gehen: zur Nachbarzeile des gelöschten
      Blocks — Backspace nach oben, Entf nach unten, und wenn es dort keine mehr
      gibt, in die andere Richtung. Ohne das fiele der Zeiger mit der Zeile aus
      der Karte und das Tippen hörte einfach auf. Der Ast der Zeilen zählt zum Block.
    */
    const { above, below } = neighboursOf(heads)
    const nachbar = towards === 1 ? (below ?? above) : (above ?? below)

    void removeMany(ids, true)

    if (nachbar) {
      setSelectedIds(new Set([nachbar.id]))
      anchorId.current = nachbar.id
      putCaretAt(inputs.current.get(nachbar.id) ?? null)
      return
    }
    // Die Liste ist leer — die offene Zeile ist der Ort der nächsten.
    setSelectedIds(new Set())
    anchorId.current = null
    openInput.current?.focus()
  }, [items, removeMany, neighboursOf])

  /**
   * Was Entf und Backspace auf einer angefassten Zeile tun — für die ganze
   * Auswahl. `towards` ist die Richtung, in die der Caret danach geht:
   * Backspace nach oben, Entf nach unten.
   */
  const handleRemoveSelection = useCallback((towards: -1 | 1) => {
    removeRows(items.filter(item => selectedIds.has(item.id)).map(item => item.id), towards)
  }, [items, selectedIds, removeRows])

  /*
    **Welche Haken der Nutzer selbst umgelegt hat** — Kennung → Stand vor dem Umlegen.
    Nur sie ticken und sprühen. Hakt die Aufgabe ihre Unteraufgaben mit ab (Kopplung,
    T14 #179), kommt der Wechsel ohne Wunsch an und bleibt still. Ist der Wechsel da,
    fällt der Eintrag weg: Der Effekt der Liste läuft nach dem des Hakens.
  */
  const [asked, setAsked] = useState<ReadonlyMap<string, boolean>>(() => new Map())
  const toggle = useCallback((id: string, count = 1) => {
    const item = items.find(i => i.id === id)
    if (item) setAsked(prev => new Map(prev).set(id, item.done))
    // Moment, nächste Runde, „Kommt wieder …" und `⌘Z` macht der Hook (`useSubtaskEditor.toggle`).
    void onToggle(id, count)
  }, [items, onToggle])
  useEffect(() => {
    const arrived = [...asked].filter(([id, before]) => items.find(i => i.id === id)?.done !== before)
    if (arrived.length === 0) return
    setAsked(prev => {
      const next = new Map(prev)
      for (const [id] of arrived) next.delete(id)
      return next
    })
  }, [items, asked])

  /*
    **Mit abgehakt → nacheinander sprühen** (nur mit `coupledSpark`, also wenn die Liste
    zu sehen ist). Kennung → Platz in ihrem Schub; der Eintrag bleibt, solange sie
    abgehakt ist, damit sich die Verzögerung am Haken nicht ändert und sein Timer läuft.
  */
  const [prevItems, setPrevItems] = useState(items)
  const [cascade, setCascade] = useState<ReadonlyMap<string, number>>(() => new Map())
  if (items !== prevItems) {
    setPrevItems(items)
    const wasDone = new Map(prevItems.map(i => [i.id, i.done]))
    const flipped = items.filter(i => i.done && wasDone.get(i.id) === false && !asked.has(i.id))
    const kept = [...cascade].filter(([id]) => items.find(i => i.id === id)?.done)
    if (flipped.length > 0 || kept.length !== cascade.size) {
      setCascade(new Map([...kept, ...flipped.map((i, k) => [i.id, k] as const)]))
    }
  }

  /** Die Leertaste hakt die ganze Auswahl ab. */
  const handleToggleSelection = useCallback(() => {
    // Eine Zeile hakt ihren Ast mit ab (#181) — angefasste Kinder darin nicht noch einmal umlegen.
    const picked = branchHeads(items, selectedIds)
    picked.forEach(item => toggle(item.id, picked.length))
  }, [items, selectedIds, toggle])

  /*
    **Herausziehen** (#204, Etappe 3): In der aufgeklappten Karte darf die Zeile die
    Checkliste verlassen. Draußen schließt sich ihre Lücke, und die Liste um die Karte
    (`SubtaskDragOutContext`) zeigt, wo sie landet. Ohne den Kontext, etwa in den Details,
    bleibt sie drin.
  */
  const dragOut = useContext(SubtaskDragOutContext)
  const canDragOut = dragOut !== null && parentId !== undefined
  const listRef = useRef<HTMLDivElement>(null)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  /*
    **Was mitgeht** (gewünscht 28.09.2026): Ist die gegriffene Zeile Teil einer Mehrfachauswahl,
    die ganze Auswahl, in Checklisten-Reihenfolge. Sonst nur sie. Die übrigen klappen zu.
  */
  const [draggingIds, setDraggingIds] = useState<readonly string[]>([])
  const draggingIdsRef = useRef<readonly string[]>([])
  /*
    **Der Ast zieht mit, nur unter Geschwistern** (#181): Die Kinder der gezogenen Zeilen klappen zu,
    und Ziel ist nur eine Zeile derselben Ebene unter demselben Eltern-Teil.
  */
  const [foldedIds, setFoldedIds] = useState<readonly string[]>([])
  const foldedRef = useRef<readonly string[]>([])
  const dropTargetsRef = useRef<ReadonlySet<string>>(new Set())
  const [outside, setOutside] = useState(false)
  const outsideRef = useRef(false)
  /*
    Die draußen abgelegten Zeilen bleiben zugeklappt, bis die Daten sie aus der Checkliste nehmen —
    sonst blitzten sie dort noch einmal auf, bevor sie am Ziel landen (gemeldet 27.09.2026).
  */
  const [leavingIds, setLeavingIds] = useState<readonly string[]>([])
  useEffect(() => {
    if (leavingIds.length === 0) return
    if (!items.some(item => leavingIds.includes(item.id))) {
      setLeavingIds([])
      return
    }
    // Hat die Liste abgelehnt, kommen sie nach einer Weile zurück.
    const timer = setTimeout(() => setLeavingIds([]), 1500)
    return () => clearTimeout(timer)
  }, [items, leavingIds])
  // Mehrere in der Checkliste umsortiert: zu, bis alle an ihrem neuen Platz geschrieben sind.
  const [settlingIds, setSettlingIds] = useState<readonly string[]>([])

  /*
    **Der echte Zeiger**, nicht Start + Zugweg: dnd-kit rechnet das Scrollen der Liste in den
    Zugweg ein — scrollte sie beim Herausziehen mit, lag die Linie weit weg vom Zeiger
    (gemeldet 27.09.2026).
  */
  const pointerRef = useRef<Point | null>(null)
  useEffect(() => {
    if (!draggingId || !canDragOut) return
    const track = (e: PointerEvent) => { pointerRef.current = { x: e.clientX, y: e.clientY } }
    document.addEventListener('pointermove', track, { passive: true })
    return () => {
      document.removeEventListener('pointermove', track)
      pointerRef.current = null
    }
  }, [draggingId, canDragOut])

  const pointerOf = (event: DragMoveEvent | DragEndEvent): Point | null => {
    if (pointerRef.current) return pointerRef.current
    const start = event.activatorEvent as PointerEvent | null
    if (!start || typeof start.clientX !== 'number') return null
    return { x: start.clientX + event.delta.x, y: start.clientY + event.delta.y }
  }
  const setAway = (value: boolean) => {
    outsideRef.current = value
    setOutside(value)
  }

  const collisionDetection = useCallback<CollisionDetection>(args => {
    if (outsideRef.current) return []
    // Die zugeklappten Mitgezogenen sind kein Ziel — der Block landet nicht in sich selbst. Und nur
    // Geschwister sind eins (#181).
    const group = draggingIdsRef.current
    const targets = dropTargetsRef.current
    const activeId = String(args.active.id)
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter(c => String(c.id) === activeId
        || (targets.has(String(c.id)) && !group.includes(String(c.id)))),
    })
  }, [])

  // Läuft ein Zug? Als Ref, weil dnd-kit nach dem Loslassen noch eine Bewegung nachreichen kann.
  const draggingRef = useRef(false)

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const id = String(event.active.id)
    draggingRef.current = true
    collapseOnRelease.current = null
    const siblings = siblingRows(items, id).map(item => item.id)
    const targets = new Set(siblings)
    // Aus der Auswahl gehen nur Geschwister der gegriffenen Zeile mit — mit ihrem Ast.
    const pickedSiblings = new Set(branchHeads(items, selectedRef.current).map(item => item.id).filter(x => targets.has(x)))
    const group = dragGroup(siblings, pickedSiblings, id)
    const folded = group.flatMap(head => descendantRows(items, head).map(item => item.id))
    dropTargetsRef.current = targets
    draggingIdsRef.current = group
    foldedRef.current = folded
    setDraggingIds(group)
    setFoldedIds(folded)
    setDraggingId(id)
  }, [items])

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    if (!canDragOut || !draggingRef.current) return
    const pointer = pointerOf(event)
    /*
      **Draußen heißt: über der Liste, aber nicht mehr über dieser Karte** — gefragt wird die
      Seite selbst, was unter dem Zeiger liegt (gemessen im E2E, 27.09.2026). Rechtecke taugen
      dafür nicht: Die Karte wächst beim Aufklappen noch eine Weile nach, ihr Inhalt ist dabei
      verschoben, und ihre Kanten passen nicht zu dem, was man sieht. Ein Zeiger über einer Zeile
      galt so als „draußen“, und aus dem Umsortieren wurde ein Herauslösen.

      Über leerer Fläche (Kopf der Seite, Rand) bleibt es Umsortieren: Herausgezogen wird dorthin,
      wo eine Aufgabe landen kann — auf eine andere Aufgabe oder zwischen zwei.

      **Unter der Liste heißt: ans Ende** (gemeldet 28.09.2026). Ist die letzte Aufgabe offen, liegt
      unter ihr keine Aufgabe mehr; „draußen“ blieb dort nur ein Streifen von 1 px an ihrer Kante.
    */
    const card = listRef.current?.closest('.task-entry')
    const board = listRef.current?.closest('.project-page__task-list-wrap')
    if (!pointer || !card || !board || typeof document.elementsFromPoint !== 'function') return
    const under = document.elementsFromPoint(pointer.x, pointer.y)
      .find(el => !el.closest('.subtask-drag-overlay-shell, .subtask-drag-overlay, .nest-landing-ghost'))
    const away = isOutsideCard(pointer, under, card, board)
    if (away) {
      const group = draggingIdsRef.current
      dragOut.move(pointer, group, parentId, items.some(item => group.includes(item.id) && item.recurrence != null))
      if (!outsideRef.current) setAway(true)
    } else if (outsideRef.current) {
      dragOut.back()
      setAway(false)
    }
  }, [canDragOut, dragOut, parentId, items])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    const group = draggingIdsRef.current.length > 0 ? draggingIdsRef.current : [String(active.id)]
    const folded = foldedRef.current
    const targets = dropTargetsRef.current
    draggingRef.current = false
    draggingIdsRef.current = []
    foldedRef.current = []
    setDraggingIds([])
    setFoldedIds([])
    setDraggingId(null)
    if (outsideRef.current && canDragOut) {
      setAway(false)
      const pointer = pointerOf(event)
      if (pointer && dragOut.drop(pointer, group, parentId)) setLeavingIds([...group, ...folded])
      return
    }
    if (!onMove || !over) return
    // Gerechnet wird unter den Geschwistern (#181) — dort landet die Zeile.
    const ids = items.map(item => item.id).filter(id => targets.has(id))
    const afterId = group.length > 1
      ? afterIdForGroupDrop(ids, group, String(active.id), String(over.id))
      : afterIdForDrop(ids, String(active.id), String(over.id))
    if (afterId === undefined) return
    // Der Block der Reihe nach: jede hinter ihren neuen Vorgänger, der schon steht (wie ⌥↑/↓). Bis
    // dahin bleiben die Mitgezogenen und die Äste zu — sonst blitzten sie am alten Platz auf.
    const settling = [...group.filter(id => id !== String(active.id)), ...folded]
    if (settling.length > 0) setSettlingIds(settling)
    void (async () => {
      try {
        for (const [i, id] of group.entries()) await onMove(id, i === 0 ? afterId : group[i - 1]!)
      } finally {
        if (settling.length > 0) setSettlingIds([])
      }
    })()
  }, [items, onMove, canDragOut, dragOut, parentId])

  const handleDragCancel = useCallback(() => {
    draggingRef.current = false
    draggingIdsRef.current = []
    foldedRef.current = []
    setDraggingIds([])
    setFoldedIds([])
    setDraggingId(null)
    if (outsideRef.current) setAway(false)
    dragOut?.cancel()
  }, [dragOut])


  /*
    **Nach dem Einschub ist die neue Zeile angefasst, nicht offen**
    (Nutzer-Vorgabe, 2026-09-20). Ein Einschub ist ein Einschub: Man hat gezielt
    eine Zeile dazwischengesetzt und ist fertig. Die **offene Zeile** geht
    zurück ans Ende, wo sie hingehört — die Enter-Kette dort bleibt unberührt.
  */
  /**
   * Wo die **offene Zeile** in diesem Bild steht.
   *
   * **Abgeleitet und nicht gesetzt** (gemeldet 2026-09-20, zweimal): Sie muss
   * genau in dem Bild ans Ende wandern, in dem die eingeschobene Zeile erscheint
   * — keines früher, keines später.
   *
   * Zu früh (schon auf die Antwort der Datenquelle hin) wird sie beim Umhängen
   * neu aufgebaut und verliert ihren Text: Das Getippte verschwindet für ein
   * Bild. Zu spät (in einem Effekt) steht sie ein Bild lang ZWISCHEN der
   * Ausgangszeile und der neuen — und springt dann sichtbar weg. Ein Zustand,
   * der erst nach dem Rendern nachgezogen wird, kann diesen Moment nicht
   * treffen; die Ableitung schon.
   */
  const insertedArrived = insertedId !== null && items.some(item => item.id === insertedId)
  const openAfter = insertedArrived ? null : openAfterId

  useEffect(() => {
    if (appendedAt === null || items.length <= appendedAt) return
    setAppendedAt(null)
    putCaretAt(openInput.current)
  }, [items, appendedAt])

  // Eingehängt (#204): Die neuen Zeilen stehen angefasst da, als hätte man sie eben geklickt.
  // Gefragt wird, wenn Zeilen kommen, und wenn etwas vorgemerkt wird, während sie schon dastehen.
  const landedIds = useRef<string[] | null>(null)
  useEffect(() => {
    const take = () => {
      const ids = takePendingSubtaskSelection(items.map(item => item.id))
      if (!ids) return
      landedIds.current = ids
      setInsertedId(ids[0]!)
    }
    take()
    return onPendingSubtaskSelection(take)
  }, [items])

  useEffect(() => {
    if (!insertedId) return
    const el = inputs.current.get(insertedId)
    // Noch nicht da — der nächste Durchlauf fragt wieder.
    if (!el) return
    setOpenAfterId(null)
    setSelectedIds(new Set(landedIds.current ?? [insertedId]))
    landedIds.current = null
    setEditingId(null)
    anchorId.current = insertedId
    putCaretAt(el)
    setInsertedId(null)
  }, [items, insertedId])

  const handleMenuOpenChange = useCallback((id: string, open: boolean) => {
    if (open) {
      menuOpen.current = true
      afterMenu.current = null
      return
    }
    // Ein Bild später: Dann gibt das Menü den Fokus nicht mehr zurück an sich.
    requestAnimationFrame(() => {
      menuOpen.current = false
      const act = afterMenu.current
      afterMenu.current = null
      if (act) act()
      // Sonst zurück in die Zeile, deren Menü es war — die Auswahl steht noch.
      else putCaretAt(inputs.current.get(id) ?? null)
    })
  }, [])

  /** Wen das Menü einer Zeile meint: die Auswahl, wenn die Zeile dazugehört — sonst sie allein. */
  const menuIdsFor = (id: string) => (selectedIds.has(id)
    ? items.filter(item => selectedIds.has(item.id)).map(item => item.id)
    : [id])

  // ⌘D (#209): Die Kopien stehen danach angefasst da, wie eingehängte Zeilen (`landedIds`).
  const duplicateRows = onDuplicate
    ? (id: string) => {
        // Ein Ast kommt als Ganzes mit (#181) — ein angefasstes Kind darin nicht doppelt.
        void onDuplicate(branchHeads(items, menuIdsFor(id)).map(item => item.id)).then(copies => {
          if (copies.length === 0) return
          landedIds.current = copies
          setInsertedId(copies[0]!)
        })
      }
    : undefined

  /**
   * Ruhen lassen oder aufwecken — Misch-Regel wie am Aufgaben-Menü: Solange eine wach ist, legt
   * es schlafen. Wecken je Zeile: Eine wiederkehrende, die über ihren Termin geschlafen hat, wacht
   * am nächsten auf.
   */
  const restRows = (meant: SubtaskItem[], rest: boolean) => {
    if (!onUpdate || meant.length === 0) return
    if (rest) {
      onUpdate(meant.map(i => i.id), { pausedAt: new Date() })
      showSuccessToast({ message: t('taskItem.pausedToast'), description: t('taskItem.pausedToastText', { count: 1 }), icon: <DormantIcon size={18} />, tone: 'rest' })
      return
    }
    const now = new Date()
    for (const i of meant) onUpdate([i.id], wakeTaskUpdate(i, now) as SubtaskChanges)
  }

  /*
    **Die Kürzel des Aufgaben-Menüs an der angefassten Zeile** (gemeldet 28.09.2026) — wie in der
    Aufgabenliste: W, ⇧W, D, ⇧D, M, T, G öffnen den Kasten unter der Zeile, R legt schlafen oder
    weckt. Sie gelten der Auswahl, zu der die Zeile gehört. Solange der Kasten offen ist, bleibt
    die Auswahl stehen, danach kehrt der Caret zurück (wie beim Menü).
  */
  /*
    **Unter einer wiederkehrenden Zeile gilt ihr Rhythmus** (#205; #181, Grill 11): auf jeder Ebene
    darunter, nicht nur unter der Aufgabe (`parentRecurring`).
  */
  const recurrenceLockedIds = useMemo(() => {
    const byId = new Map(items.map(item => [item.id, item]))
    const locked = new Set<string>()
    for (const item of items) {
      for (let up = item.parentId ? byId.get(item.parentId) : undefined; up; up = up.parentId ? byId.get(up.parentId) : undefined) {
        if (up.recurrence != null) { locked.add(item.id); break }
      }
    }
    return locked
  }, [items])
  const recurrenceLocked = useCallback(
    (ids: readonly string[]) => parentRecurring || ids.some(id => recurrenceLockedIds.has(id)),
    [parentRecurring, recurrenceLockedIds],
  )

  const [shortcut, setShortcut] = useState<{ id: string; target: TaskMenuTarget } | null>(null)
  const handleMenuShortcut = useCallback((id: string, target: TaskMenuTarget) => {
    if (target === 'recurrence' && recurrenceLocked(selectedRef.current.has(id) ? [...selectedRef.current] : [id])) return
    handleMenuOpenChange(id, true)
    setShortcut({ id, target })
  }, [recurrenceLocked, handleMenuOpenChange])
  // Als Ref: Wert geliefert UND Kasten zu melden sich beide — zurückgekehrt wird einmal.
  const shortcutRef = useRef(shortcut)
  shortcutRef.current = shortcut
  const closeShortcut = useCallback(() => {
    const current = shortcutRef.current
    if (!current) return
    shortcutRef.current = null
    setShortcut(null)
    handleMenuOpenChange(current.id, false)
  }, [handleMenuOpenChange])

  const reduced = useReducedMotion() ?? false
  const shortcutText = useShortcutText()
  const rowIds = useMemo(() => items.map(item => item.id), [items])

  /*
    **Die Tasten des Baums unter der Checkliste** (Wunsch Sven, 28.09.2026; #181): nur, solange eine
    Zeile angefasst ist — dann gelten sie. Beim Schreiben nicht: Dort ist ⌥←/→ das Springen um ein
    Wort.

    **Ihr Platz steht immer** (Sven, 04.10.2026): Mal stehen die Tasten darin, mal ist er leer. Fuhr
    die Leiste auf und zu, wuchs und schrumpfte das ganze Fenster bei jedem Anfassen und Schreiben.
    Nur ein Entwurf (ohne Ein- und Ausrücken) hat keinen.
  */
  const showKeys = handleLevel !== undefined && selectedIds.size > 0 && editingId === null && draggingId === null
  const keys = [
    { keys: [shortcutText('⌥→')], label: t('taskItem.subtaskTree.keys.indent') },
    { keys: [shortcutText('⌥←')], label: t('taskItem.subtaskTree.keys.outdent') },
    ...(onMove ? [{ keys: [shortcutText('⌥↑↓')], label: t('taskItem.subtaskTree.keys.move') }] : []),
    ...(onUnnest ? [{ keys: [shortcutText('⇧⌥↑↓')], label: t('taskItem.subtaskTree.keys.unnest') }] : []),
  ]

  /**
   * Das Abbild: dieselbe leere Zeile, aber ohne Feld und ohne Griff — sie ist
   * nur ein Bild, das zufährt. `aria-hidden`, weil für Vorlesegeräte nichts
   * geschieht: Die echte offene Zeile ist eine Zeile höher gewandert.
   *
   * **Eigene Klasse und nicht `--open`:** Die **offene Zeile** gibt es genau
   * einmal, und das soll auch beim Zählen stimmen. Das Abbild ist keine.
   */
  const ghostRow = (
    <div className="subtask-row subtask-row--ghost" aria-hidden>
      <TaskCheck checked={false} onCheckedChange={() => {}} kind="subtask" placeholder disabled />
    </div>
  )

  /*
    **Der Baum** (#181, #211): Die offene Zeile steht mitten im Baum als Geschwister der Zeile, hinter
    der sie entsteht — also hinter deren Ast und auf deren Ebene. Die Linien rechnen sie mit.
  */
  const openAnchor = openAfter ? items.find(item => item.id === openAfter) : undefined
  const openSlot = openAnchor ? lastOfBranch(items, openAnchor.id)?.id ?? null : null
  const openRowTree = { id: OPEN_ROW_KEY, depth: openAnchor?.depth ?? 1, parentId: openAnchor?.parentId }
  const lines = treeLines(openSlot === null
    ? [...items, openRowTree]
    : items.flatMap(item => (item.id === openSlot ? [item, openRowTree] : [item])))

  const openRow = (
    <OpenRow
      ref={openInput}
      lines={lines.get(OPEN_ROW_KEY)}
      itemCount={items.length}
      onAdd={title => {
        if (openAfterId && onInsertAfter) {
          // Die offene Zeile bleibt, wo sie ist, bis die neue Zeile dasteht —
          // siehe den Effekt oben. Sonst verliert sie beim Umhängen ihren Text.
          void onInsertAfter(openAfterId, title).then(id => { if (id) setInsertedId(id) })
          return
        }
        // Am Ende: Der Zeiger soll hier bleiben, wenn die Zeile ankommt.
        setAppendedAt(items.length)
        onAdd(title)
      }}
      onLeaveEmpty={() => setOpenAfterId(null)}
    />
  )

  /**
   * Die Kinder der Liste, flach und in der Reihenfolge, in der sie dastehen.
   *
   * Die **offene Zeile** trägt dabei immer denselben Schlüssel, egal wo sie
   * steht — sie ist ein Ding, das seinen Platz wechselt, und kein zweites, das
   * an anderer Stelle entsteht.
   */
  const awayOf = (id: string): 'lifted' | 'away' | undefined => {
    if (leavingIds.includes(id) || settlingIds.includes(id) || foldedIds.includes(id)) return 'away'
    if (draggingIds.length > 1 && id !== draggingId && draggingIds.includes(id)) return 'away'
    if (canDragOut && draggingId === id) return outside ? 'away' : 'lifted'
    return undefined
  }

  const rows: { key: string; node: React.ReactNode; appear?: boolean }[] = []
  for (const item of items) {
    const row = (
      <SubtaskRow
        item={item}
        selected={selectedIds.has(item.id)}
        editing={editingId === item.id}
        onToggle={toggle}
        asked={asked.has(item.id)}
        sparkDelay={coupledSpark && cascade.has(item.id) ? (cascade.get(item.id)! + 1) * SPARK_STEP_MS : null}
        onRename={onRename}
        onRemove={handleRemove}
        onSelect={handleSelect}
        onRelease={handleRelease}
        onEdit={handleEdit}
        onEndEdit={handleEndEdit}
        onOpenBelow={handleOpenBelow}
        onNudge={handleNudge}
        onStep={handleStep}
        onToggleSelection={handleToggleSelection}
        onRemoveSelection={handleRemoveSelection}
        registerInput={registerInput}
        onUnnest={handleUnnest}
        away={awayOf(item.id)}
        focusPrevious={focusPrevious}
        focusNext={focusNext}
        onOpen={onOpen}
        onOpenComments={onOpenComments}
        onOpenReminders={onOpenReminders}
        onMenuShortcut={onUpdate ? handleMenuShortcut : undefined}
        onRestShortcut={onUpdate ? (id => {
          const meant = items.filter(i => menuIdsFor(id).includes(i.id))
          restRows(meant, !meant.every(i => i.pausedAt != null))
        }) : undefined}
        onDuplicate={duplicateRows}
        lines={lines.get(item.id)}
        onLevel={handleLevel}
      />
    )
    const ids = onUpdate ? menuIdsFor(item.id) : []
    const meant = items.filter(i => ids.includes(i.id))
    rows.push({
      key: item.id,
      // Die frisch abgeschickte Zeile steht schon da — als offene Zeile, mit
      // ihrem Text. Sie tritt an deren Stelle, sie kommt nicht an.
      appear: item.id !== insertedId,
      node: !onUpdate ? row : (
        <SubtaskMenu
          subtask={item}
          marks={taskMenuMarks(meant)}
          onUpdate={changes => onUpdate(ids, changes)}
          // Misch-Regel wie am Aufgaben-Menü: Solange eine wach ist, legt die Zeile schlafen.
          dormant={meant.every(i => i.pausedAt != null)}
          parentRecurring={recurrenceLocked(ids)}
          movingRecurring={meant.some(i => i.recurrence != null)}
          onRest={rest => restRows(meant, rest)}
          shortcut={shortcut?.id === item.id ? shortcut.target : null}
          onShortcutClose={closeShortcut}
          onUnnest={handleUnnest ? () => { afterMenu.current = () => handleUnnest(item.id, 'below') } : undefined}
          parentId={parentId}
          onMoveTo={onMoveTo ? target => { afterMenu.current = () => onMoveTo(ids, target) } : undefined}
          onDelete={() => { afterMenu.current = () => removeRows(ids, 1) }}
          reminderTaskId={meant.length === 1 ? item.id : undefined}
          onOpenReminders={onOpenReminders ? () => { afterMenu.current = () => onOpenReminders(item.id, 'at') } : undefined}
          onIndent={handleLevel ? () => { afterMenu.current = () => handleLevel(item.id, 'indent') } : undefined}
          onOutdent={handleLevel && meant.some(i => (i.depth ?? 1) > 1)
            ? () => { afterMenu.current = () => handleLevel(item.id, 'outdent') }
            : undefined}
          onOpenChange={open => handleMenuOpenChange(item.id, open)}
          // Beim Schreiben gehört der Rechtsklick dem Textfeld.
          disabled={editingId === item.id}
        >
          {/* Ohne eigenen Kasten (`display: contents`): Der Rechtsklick steigt von der Zeile hier auf. */}
          <div className="subtask-list__menu-target">{row}</div>
        </SubtaskMenu>
      ),
    })
    if (openSlot === item.id) rows.push({ key: OPEN_ROW_KEY, node: openRow })
  }
  if (openSlot === null) rows.push({ key: OPEN_ROW_KEY, node: openRow })
  // Das Abbild steht nur an dem einen Bild, an dem die echte Zeile nach oben geht.
  else if (ghostBelow) rows.push({ key: GHOST_ROW_KEY, node: ghostRow, appear: false })

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      measuring={draggingIds.length > 1 || foldedIds.length > 0 ? MEASURE_ALWAYS : undefined}
      modifiers={canDragOut ? undefined : [onlyVertical]}
      /*
        **In der Karte scrollt die Liste erst, wenn die Zeile draußen ist** (gemessen im E2E,
        27.09.2026). Nah am Rand scrollte sie sonst schon beim Umsortieren — die Checkliste
        rutschte unter dem Zeiger weg, er stand „draußen“, und aus dem Umsortieren wurde ein
        Herauslösen. Draußen braucht es das Scrollen, um ein Ziel weiter oben/unten zu erreichen.
      */
      autoScroll={canDragOut ? outside : true}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
        <div ref={listRef} className={`subtask-list${draggingId ? ' subtask-list--dragging' : ''}`} onBlur={handleBlur}>
          {/*
            **Die Auswahl-Hülle** (#293) wie in der Aufgabenliste. Die Zeile, in der geschrieben
            wird, bleibt draußen (wie bisher ungetönt).
          */}
          <SelectionHull
            kind="subtask"
            rowIds={rowIds}
            selectedIds={selectedIds}
            excludedId={editingId}
            rowAttribute="data-subtask-id"
            suspended={draggingId !== null}
          />
          {/*
            **Eine flache Liste von Kindern, kein Fragment je Zeile.** Nur so
            behält die **offene Zeile** ihren Platz im Baum, wenn sie von der
            Mitte ans Ende wandert: Gleicher `key`, gleiche Instanz — React ordnet
            um, statt sie abzubauen und neu zu bauen. Genau daran verlor sie
            früher ihren getippten Text.
          */}
          <AnimatePresence initial={false}>
            {rows.map(row => (
              <MotionRow
                key={row.key}
                reduced={reduced}
                appear={row.appear}
                /*
                  **Die offene Zeile fährt bei jedem Platzwechsel auf.** Sie
                  behält ihren Schlüssel (und damit ihren Text), hat deshalb kein
                  Ankommen — ohne diesen Anstoß stünde sie an ihrem neuen Platz
                  einfach so da, während jede andere Zeile Platz macht.

                  Das gilt für beide Wege: hinauf, wenn Cmd/Ctrl+Enter sie in die
                  Mitte holt, und zurück ans Ende, sobald ihr Text zur Zeile
                  geworden ist. Ihren alten Platz nimmt dabei jeweils etwas
                  anderes ein — oben die neue Zeile, unten ihr **Abbild**.
                */
                replay={row.key === OPEN_ROW_KEY ? (openAfter ?? 'ende') : undefined}
              >
                {row.node}
              </MotionRow>
            ))}
          </AnimatePresence>
        </div>
      </SortableContext>
      {handleLevel !== undefined && (
        <div className="subtask-list__keys">
          <AnimatePresence initial={false}>
            {showKeys && (
              <motion.div key="keys" {...(reduced ? keysStill : keysArrive)}>
                <KeysHint align="start" items={keys} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      {/* Frei beweglich und über der ganzen Seite: Die Zeile soll aus der Karte heraus können. */}
      {canDragOut && createPortal(
        <DragOverlay dropAnimation={null} className="subtask-drag-overlay-shell">
          {draggingId && (
            <div className="subtask-drag-stack">
              {draggingIds.length >= 3 && <div className="subtask-drag-stack__back subtask-drag-stack__back--2" />}
              {draggingIds.length >= 2 && <div className="subtask-drag-stack__back subtask-drag-stack__back--1" />}
              <RowImage id={draggingId} />
              {draggingIds.length > 1 && <span className="subtask-drag-stack__badge">{draggingIds.length}</span>}
            </div>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  )
}
